import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from './Breadcrumbs';
import { useIsCompact } from '../lib/useMediaQuery';

/**
 * TopBar — sticky top app bar (Small variant).
 *
 * Derived from M3 Top App Bar (Small variant) per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/top-app-bar/overview
 *   - Reference: github.com/material-components/material-web (no
 *                first-party top-app-bar component at time of authoring;
 *                M3 spec drives the visual)
 *
 * Populus adaptations applied:
 *   - White surface (--color-card) vs M3 dynamic surface (M3 defaults
 *     to primary container; Populus chose white for cleaner business-app
 *     aesthetic + better contrast against the dark Sidebar)
 *   - Breadcrumbs left-aligned in the headline slot (vs M3 page title;
 *     breadcrumbs serve the same orientation function in Populus's
 *     deeper-hierarchy entity-detail flows)
 *   - A leading "Back" affordance on any page that has a logical parent (the
 *     breadcrumb trail is deeper than one crumb). It lives in the shell — not
 *     each page body — so every detail page gets a reliable back control. It
 *     navigates to the breadcrumb's parent crumb (true "up" to the owning
 *     entity), falling back to browser history when the parent has no link.
 *   - Right-aligned action cluster (notifications, user menu) — M3
 *     trailing icon convention
 *   - Sticky positioning (M3 implies but doesn't require)
 *   - Height 56px (M3 Small Top App Bar spec)
 *   - Subtle bottom border instead of elevation shadow (vs M3 elevation
 *     level 0-2; cleaner for business-app density)
 *
 * Token discipline:
 *   - Surface → --color-card (system)
 *   - Border → --color-border (system)
 *   - All chrome → --color-* (system; TopBar is operator-tool plumbing,
 *     not a brand surface)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface TopBarProps {
  breadcrumbs?: BreadcrumbItem[];
  title?: ReactNode;
  actions?: ReactNode;
  /** Compact-only: opens the off-canvas sidebar drawer. When set, a menu
   *  button renders at the head of the bar (see shell/AppLayout.tsx). */
  onMenuClick?: () => void;
}

export function TopBar({ breadcrumbs, title, actions, onMenuClick }: TopBarProps) {
  const compact = useIsCompact();
  const navigate = useNavigate();
  const location = useLocation();

  // A UNIVERSAL Back affordance: it always steps back through browser history,
  // never a breadcrumb-derived "parent". The breadcrumb trail resets on some
  // cross-entity flows, which used to send Back to the wrong page or hide it
  // entirely — so visibility and target are both decoupled from the crumbs.
  // Shown on every page except the dashboard home (where there's nowhere back).
  const canGoBack = location.pathname !== '/';

  function goBack() {
    navigate(-1);
  }

  const iconButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    flexShrink: 0,
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--r-md)',
    background: 'var(--color-card)',
    color: 'var(--color-ink-2)',
    cursor: 'pointer',
  } as const;

  return (
    <header style={{
      gridArea: 'topbar',
      position: 'sticky',
      top: 0,
      zIndex: 5,
      height: 56,
      display: 'flex',
      alignItems: 'center',
      // Tighter gap/padding on phones so the breadcrumb + action cluster
      // (View-as / Sign out) never overflow the 56px bar off the right edge.
      gap: compact ? 'var(--sp-2)' : 'var(--sp-4)',
      padding: compact ? '0 var(--sp-3)' : '0 var(--sp-5)',
      background: 'var(--color-card)',
      borderBottom: '1px solid var(--color-border)',
      // Belt-and-suspenders: the bar itself never scrolls the document — its
      // children shrink/truncate instead (breadcrumb ellipsizes, actions stay
      // reachable). Prevents the reported off-canvas "Sign out" clip.
      overflow: 'hidden',
    }}>
      {onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            flexShrink: 0,
            marginLeft: 'calc(-1 * var(--sp-1))',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            background: 'var(--color-card)',
            color: 'var(--color-ink-2)',
            cursor: 'pointer',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}
      {canGoBack && (
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          title="Back"
          style={{ ...iconButtonStyle, marginLeft: onMenuClick ? 0 : 'calc(-1 * var(--sp-1))' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <Breadcrumbs items={breadcrumbs} collapse={compact} />
        ) : title ? (
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-lg)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-ink)',
            letterSpacing: 'var(--ls-tight)',
          }}>{title}</h1>
        ) : null}
      </div>
      {actions && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 'var(--sp-1)' : 'var(--sp-2)',
          flexShrink: 0,
        }}>
          {actions}
        </div>
      )}
    </header>
  );
}
